// fix/pwa-error-boundary -- top-level + per-route React error boundary.
//
// Class component because that's the only React pattern that catches
// render-time exceptions. Wraps children; if anything throws during
// render or in a lifecycle method, the boundary catches it via
// getDerivedStateFromError + componentDidCatch and renders a themed
// fallback with a manual Reload button.
//
// Usage:
//   <ErrorBoundary label="app-root"><App /></ErrorBoundary>
//   <ErrorBoundary label="hermes" compact><Suspense><HermesPanel /></Suspense></ErrorBoundary>
//
// Props:
//   children        -- what to render normally
//   label           -- short string included in console.error for triage
//   compact         -- smaller fallback (used for per-route boundaries
//                      so a crashing panel doesn't fill the whole shell)
//   renderFallback  -- optional ({error, reset, label}) => ReactNode for
//                      callers that want full control

import { Component, useEffect, useReducer } from "react";
import { tokens, subscribe } from "./cf_theme.js";
import { isChunkLoadError } from "./error_handling.js";

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    try {
      // eslint-disable-next-line no-console
      console.error(
        "[ErrorBoundary]",
        this.props.label || "(unlabeled)",
        error,
        info && info.componentStack ? info.componentStack : ""
      );
    } catch { /* never let logging itself throw past here */ }
  }
  reset = () => this.setState({ error: null });
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (typeof this.props.renderFallback === "function") {
      return this.props.renderFallback({ error, reset: this.reset, label: this.props.label });
    }
    return (
      <ErrorFallback
        error={error}
        reset={this.reset}
        label={this.props.label}
        compact={!!this.props.compact}
      />
    );
  }
}

// Themed fallback. Subscribes to cf_theme so a light/dark flip after
// the error re-paints the fallback in the new theme without needing
// the user to dismiss + retry.
function ErrorFallback({ error, reset, label, compact }) {
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribe(() => bump()), []);
  const t = tokens();
  const isChunk = isChunkLoadError(error);
  const title = isChunk
    ? "A new version of the app is available."
    : "Something went wrong.";
  const subtitle = isChunk
    ? "The piece you were trying to open was updated on the server. Reload to get the new version."
    : "We couldn't render this view. Try again or reload the page.";
  const onHardReload = () => { try { window.location.reload(); } catch {} };

  // Compact variant fills only the parent pane (used when wrapping a
  // specific lazy panel so a Skills-tab crash doesn't blank the shell).
  const wrap = compact
    ? { flex: 1, padding: 24, display: "flex", alignItems: "center", justifyContent: "center" }
    : { minHeight: "100vh", padding: 32, display: "flex", alignItems: "center", justifyContent: "center" };

  return (
    <div role="alert" aria-live="assertive" style={{
      ...wrap,
      background: t.surface,
      color: t.text_primary,
      fontFamily: "Outfit, system-ui, sans-serif",
    }}>
      <div style={{
        maxWidth: 480,
        width: "100%",
        background: t.surface_alt,
        border: `1px solid ${t.border}`,
        borderRadius: 14,
        padding: 24,
        boxShadow: compact ? "none" : "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{
          fontSize: 11, color: t.text_disabled, letterSpacing: 0.5,
          textTransform: "uppercase", fontFamily: "monospace", marginBottom: 8,
        }}>
          {label ? `Error in ${label}` : "Error"}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{title}</div>
        <div style={{ color: t.text_muted, fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
          {subtitle}
        </div>
        {/* Reload is a real <button> (per spec). autoFocus so keyboard
            users can press Enter to recover. */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            autoFocus
            onClick={onHardReload}
            style={{
              padding: "8px 16px", border: "none", borderRadius: 8,
              background: t.accent, color: t.accent_fg,
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              fontFamily: "inherit",
            }}>
            Reload
          </button>
          {!isChunk && (
            <button
              type="button"
              onClick={reset}
              style={{
                padding: "8px 14px", border: `1px solid ${t.border}`,
                borderRadius: 8, background: "transparent", color: t.text_muted,
                fontSize: 13, cursor: "pointer", fontFamily: "inherit",
              }}>
              Try again
            </button>
          )}
        </div>
        {/* Compact error message for debugging. Hidden behind <details>
            so it doesn't dominate the fallback for non-technical users. */}
        <details style={{ marginTop: 16 }}>
          <summary style={{ fontSize: 11, color: t.text_disabled, cursor: "pointer", fontFamily: "monospace" }}>
            Technical details
          </summary>
          <pre style={{
            fontSize: 10, color: t.text_disabled, marginTop: 8,
            whiteSpace: "pre-wrap", wordBreak: "break-word",
            background: t.surface, border: `1px solid ${t.border}`,
            borderRadius: 6, padding: 8, maxHeight: 200, overflow: "auto",
            fontFamily: "monospace",
          }}>
            {error && (error.message || String(error))}
          </pre>
        </details>
      </div>
    </div>
  );
}
